/**
 * Users service
 *
 * @version
 * 4.77.0 OAUI-725 Implement custom tab for list orders page
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.36.0 OAUI-502 Edit order details
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { isEmpty } from 'lodash';
import { IUserModel } from '../../interfaces/users/user-interfaces';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  IBcxErrorService,
  IBcxOauth2HttpService,
} from '../../interfaces/generic/generic-interfaces';
import { WdLocalSharedFunctionsService } from '../shared/wd-local-shared-functions.service';
import { IOrderTabs } from '../../interfaces/orders/order-interfaces';
import { WdLocalEnvironmentService } from '../environment/wd-local-environment.service';

@Injectable({
  providedIn: 'root',
})
export class WdUserService {
  // Booleans
  isSavingCustomTab = new BehaviorSubject(false);
  isSavingDefaultTabs = new BehaviorSubject(false);
  isRemovingCustomTab = new BehaviorSubject(false);
  currentUser = new BehaviorSubject<any | null>(null);

  /**
   * Constructor Initialization
   * @since 4.77.0
   */
  constructor(
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    private translate: TranslateService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdOauth2HttpService')
    public wdOauth2HttpService: IBcxOauth2HttpService,
  ) {
    void this.initialize();
  }

  /**
   * Initialize the service with user data
   * Fetch the current user data if none provided
   */
  async initialize(user?: any): Promise<void> {
    const currentUser = user
      ? user
      : await this.getUserById(this.wdEnvironmentService.user.index);
    // Get current logged-in user based on ID
    this.currentUser.next(currentUser);
  }

  /**
   * Parse User
   *
   * @since 4.36.0
   * @memberof WdUserService
   */
  static parseUser(user: any): IUserModel {
    return {
      id: user.id,
      name: {
        first: user.firstName,
        last: user.lastName,
      },
      fullName: user.firstName + ' ' + user.lastName,
    };
  }

  /**
   * Parse Users
   *
   * @since 4.36.0
   * @memberof WdUserService
   */
  parseUsers(usersArr: any): Array<any> {
    let users: any = [];

    if (!isEmpty(usersArr)) {
      users = usersArr.map((user: any) => {
        return WdUserService.parseUser(user);
      });
    }

    return users;
  }

  /**
   * GET user based on id
   *
   * @since 4.77.0
   * @memberof WdUserService
   */
  async getUserById(id: string): Promise<any> {
    try {
      const params = `getSettings=true`;
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctions.userApiUrl}users/${id}${
          params ? '?' + params : ''
        }`,
      );
      return response.data;
    } catch (error) {
      this.wdErrorService.userError('Error', 'Failed to load user');
    }
  }

  /**
   * Add or update user custom tab
   * Passing a tab key in the customTab parameter updates a tab, otherwise adds a new tab
   * @since 4.77.0
   * @memberof WdUserService
   */
  async manageOrderListCustomTab(
    userId: string,
    customTab: IOrderTabs,
  ): Promise<void> {
    // Toggle Loader
    this.isSavingCustomTab.next(true);
    try {
      // Update user custom tab
      const userResponse = await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.userApiUrl}users/settings/${userId}`,
        { orderListCustomTab: customTab },
      );
      // Re-initialize updated user
      await this.initialize(userResponse.data);
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyUpdated', {
          type: this.translate.instant('order.tabs.custom.title.singular'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      // throw error;
    } finally {
      // Toggle Loader
      this.isSavingCustomTab.next(false);
    }
  }

  /**
   * Remove user custom tab
   * @since 4.77.0
   * @memberof WdUserService
   */
  async removeOrderListCustomTab(
    userId: string,
    customTabKey: string,
  ): Promise<void> {
    // Toggle Loader
    this.isRemovingCustomTab.next(true);
    try {
      // Update user custom tab
      const userResponse = await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.userApiUrl}users/settings/${userId}`,
        { removeCustomTab: customTabKey },
      );
      // Re-initialize updated user
      await this.initialize(userResponse.data);
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyDeleted', {
          type: this.translate.instant('order.tabs.custom.title.singular'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      // throw error;
    } finally {
      // Toggle Loader
      this.isRemovingCustomTab.next(false);
    }
  }

  /**
   * Save default tab settings
   */
  async saveDefaultTabSettings(
    userId: string,
    defaultTabList: Array<IOrderTabs>,
  ): Promise<void> {
    // Toggle Loader
    this.isSavingDefaultTabs.next(true);
    try {
      // Update user default tabs
      const userResponse = await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.userApiUrl}users/settings/${userId}`,
        { orderListDefaultTabs: defaultTabList },
      );
      // Re-initialize updated user
      await this.initialize(userResponse.data);
      // Success Message
      this.wdErrorService.userSuccess(
        this.translate.instant('labels.success'),
        this.translate.instant('outputs.successfullyUpdated', {
          type: this.translate.instant('order.tabs.default.title.plural'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.translate.instant('labels.error'),
        error.statusText,
      );
      // throw error;
    } finally {
      // Toggle Loader
      this.isSavingDefaultTabs.next(false);
    }
  }
}
